<template>
  <div class="policies-table">
    <md-table-toolbar>
      <div v-if="!isReadOnly" class="md-toolbar-section-start add-new-policy">
        <md-button @click="showCreateEditPolicyDialog = true">
          <md-icon>add</md-icon>
          Add new policy
        </md-button>
      </div>

      <md-field md-clearable class="md-toolbar-section-end">
        <md-input v-model="policySearchQuery" placeholder="Search by conditions..." />
        <md-button class="md-icon-button md-dense md-primary tabbed-table-actions" disabled>
          <md-icon>search</md-icon>
        </md-button>
      </md-field>
    </md-table-toolbar>

    <md-table v-model="policies" table-header-color="purple" md-card>
      <md-table-empty-state md-icon="warning" md-label="No polices found!" :md-description="emptyStateDescription">
        <md-button
          v-if="!policySearchQuery && !isReadOnly"
          class="md-primary md-raised"
          @click="showCreateEditPolicyDialog = true"
        >
          <md-icon>add</md-icon>
          Add new policy
        </md-button>
      </md-table-empty-state>

      <md-table-row slot="md-table-row" slot-scope="{ item }" v-if="item.actionCondition !== 'True'">
        <md-table-cell>
          <md-switch
            @change="patchPolicy(item.id, !item.enabled)"
            class="md-primary"
            v-bind:value="!item.enabled"
            :disabled="isReadOnly"
          ></md-switch>
        </md-table-cell>

        <md-table-cell md-label="Action" class="text-center">
          <i v-if="item.action === 'allow'" class="flaticon-006-shield-1 flaticon-sec" aria-hidden="true">
            <md-tooltip md-direction="top">bypass</md-tooltip>
          </i>
          <i v-else-if="item.action === 'deny'" class="flaticon-005-error-1 flaticon-sec" aria-hidden="true">
            <md-tooltip md-direction="top">deny</md-tooltip>
          </i>
          <i v-else-if="item.action === 'mfa'" class="flaticon-023-lock flaticon-sec" aria-hidden="true">
            <md-tooltip md-direction="top">mfa</md-tooltip>
          </i>
        </md-table-cell>

        <md-table-cell md-label="Condition">
          <!--suppress HtmlUnknownAttribute -->
          <pre v-highlightjs="item.actionCondition"><code class="python"></code></pre>
        </md-table-cell>

        <!-- Actions -->
        <md-table-cell>
          <!-- TODO Add Edit Functionality -->
          <md-button :disabled="isReadOnly" class="md-icon-button" @click.stop="deletePolicy(item.id)">
            <md-tooltip md-direction="top">Delete Policy</md-tooltip>
            <md-icon>delete</md-icon>
          </md-button>
        </md-table-cell>
      </md-table-row>
    </md-table>

    <div class="md-layout md-alignment-center">
      <pagination v-model="currentPage" :page-count="totalPages" @input="changePage"></pagination>
    </div>

    <create-edit-policy
      :show="showCreateEditPolicyDialog"
      @close="showCreateEditPolicyDialog = false"
      :scope="currentScope"
      @done="
        showCreateEditPolicyDialog = false;
        fetchPolicies();
      "
    ></create-edit-policy>

    <delete-dialog
      :show="showDeleteDialog"
      delete-api-name="deletePolicy"
      title="Delete Policy"
      message="This action deletes this policy permanently, Are you sure?"
      :request-body="{ policy_pk: deletePolicyId }"
      @close="
        showDeleteDialog = false;
        deletePolicyId = undefined;
      "
      @deleted="policyDeleted"
    ></delete-dialog>
  </div>
</template>

<script>
import { computed, onMounted, ref, watch } from "@vue/composition-api";
import apiManager from "../../api-manager";
import fetchErrorHandler from "../../error";
import snakeCaseObjectKeysToCamelCase from "../../utils/snakeCaseObjectKeysToCamelCase";
import camelCaseObjectKeysToSnakeCase from "../../utils/camelCaseObjectKeysToSnakeCase";
import { Pagination } from "@/components";
import CreateEditPolicy from "@/components/Policies/CreateEditPolicy";
import NotificationStore from "../NotificationPlugin/NotificationStore";
import DeleteDialog from "@/components/DeleteDialog";

export default {
  name: "PoliciesTable",
  props: {
    query: {
      type: Object,
    },
    scopeName: {
      type: String,
    },
    isDeletedApplication: {
      type: Boolean,
    },
  },
  components: { Pagination, CreateEditPolicy, DeleteDialog },
  setup(props) {
    const getPoliciesParams = computed(() => {
      if (policySearchQuery.value)
        return {
          ...camelCaseObjectKeysToSnakeCase({
            ...props.query,
            mfaSettings: false,
            page: currentPage.value,
          }),
          action_condition__icontains: policySearchQuery.value,
        };
      else return camelCaseObjectKeysToSnakeCase({ ...props.query, mfaSettings: false, page: currentPage.value });
    });

    const policies = ref([]);
    const fetchPolicies = async () => {
      const response = await apiManager.getPolicies({
        params: getPoliciesParams.value,
      });
      if (response.status !== 200) {
        fetchErrorHandler(response.status, response.statusText, "PolicyTable");
        policies.value = [];
      } else {
        const parsedResponse = snakeCaseObjectKeysToCamelCase(response.parsedData);
        policies.value = parsedResponse.policies;
        totalPages.value = parsedResponse.totalPages;
      }
    };

    const currentPage = ref(1);
    const totalPages = ref(1);
    const changePage = (page) => {
      currentPage.value = page;

      fetchPolicies();
    };

    const patchPolicy = async (policyId, enabled) => {
      const response = await apiManager.patchPolicy({
        params: camelCaseObjectKeysToSnakeCase({
          policyPk: policyId,
          enabled: enabled,
        }),
      });
      if (response.status === 200) {
        await fetchPolicies();
      } else if (response.status === 400) {
        NotificationStore.notify({
          message: response.parsedError,
          icon: "info",
          horizontalAlign: "right",
          verticalAlign: "top",
          type: "danger",
        });
      } else {
        fetchErrorHandler(response.status, response.statusText, "PolicyTable");
      }
    };

    const deletePolicyId = ref(undefined);
    const showDeleteDialog = ref(false);
    const deletePolicy = async (policyId) => {
      deletePolicyId.value = policyId;
      showDeleteDialog.value = true;
    };
    const policyDeleted = () => {
      showDeleteDialog.value = false;

      if (policies.value.length === 1) {
        if (currentPage.value !== 1) changePage(currentPage.value - 1);
        else changePage(1);
      } else {
        fetchPolicies();
      }
    };

    const showCreateEditPolicyDialog = ref(false);
    const currentScope = computed(() => {
      if (props) {
        if (props.query.applicationPk)
          return {
            type: "application",
            name: props.scopeName,
            params: {
              applicationPk: props.query.applicationPk,
            },
          };
        else if (props.query.applicationUserPk)
          return {
            type: "user",
            name: props.scopeName,
            params: {
              applicationUserPk: props.query.applicationUserPk,
            },
          };
      }

      return undefined;
    });

    const policySearchQuery = ref("");
    watch(policySearchQuery, () => {
      fetchPolicies();
    });

    const emptyStateDescription = computed(() => {
      return policySearchQuery.value
        ? "No policies found for this query. Please try another search term."
        : "No policies found. Please add one using the button bellow.";
    });

    const isReadOnly = computed(() => {
      return window.localStorage.getItem("adminGroups").includes("viewer_admins") || props.isDeletedApplication;
    });

    onMounted(() => {
      fetchPolicies();
    });

    return {
      policies,
      fetchPolicies,

      totalPages,
      currentPage,
      changePage,

      patchPolicy,

      showDeleteDialog,
      deletePolicy,
      deletePolicyId,
      policyDeleted,

      showCreateEditPolicyDialog,
      currentScope,

      policySearchQuery,

      emptyStateDescription,

      isReadOnly,
    };
  },
};
</script>

<style lang="scss">
.md-tabs-content .policies-table {
  table thead {
    display: contents;
  }
}

.policies-table {
  width: 98%;
  margin: auto;

  .hljs {
    background: none;
    padding: 0;
    font-size: 15px;
  }

  .flaticon-sec:before {
    font-size: 28px;
  }

  .flaticon-sec {
    color: rgba(0, 0, 0, 0.7);
  }

  .md-field .tabbed-table-actions .md-icon {
    color: #999999 !important;
  }

  .md-toolbar.md-table-toolbar {
    padding: 0;
  }

  .md-table-cell {
    padding: 6px 10px;
  }

  .md-table-head-label {
    padding: 4px;
  }
}
</style>
